// Do not add a dependency on the uiConfig Tabs organism — this atom must stay
// independent. Panel linkage: render the corresponding panel with
// aria-labelledby="tab-{id}" and role="tabpanel".
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { useRef } from 'react'
import styles from './UnderlineTabs.module.less'

export type UnderlineTabItem<TId extends string = string> = {
  id: TId
  label: string
  disabled?: boolean
}

type UnderlineTabsProps<TId extends string = string> = {
  items: ReadonlyArray<UnderlineTabItem<TId>>
  activeId: TId
  onSelect: (id: TId) => void
  className?: string
}

export function UnderlineTabs<TId extends string>({
  items,
  activeId,
  onSelect,
  className,
}: UnderlineTabsProps<TId>): ReactNode {
  const tablistRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, itemId: TId) => {
    const arrowKeys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End']
    if (!arrowKeys.includes(event.key)) return
    event.preventDefault()
    const enabledItems = items.filter((item) => !item.disabled)
    const currentIndex = enabledItems.findIndex((item) => item.id === itemId)
    if (currentIndex === -1) return
    let nextIndex: number
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % enabledItems.length
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + enabledItems.length) % enabledItems.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = enabledItems.length - 1
        break
      default:
        return
    }
    const nextItem = enabledItems[nextIndex]
    onSelect(nextItem.id)
    tablistRef.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(`tab-${nextItem.id}`)}`)?.focus()
  }

  return (
    <div ref={tablistRef} role="tablist" className={[styles.UnderlineTabs, className].filter(Boolean).join(' ')}>
      {items.map((item) => {
        const isActive = item.id === activeId
        return (
          <button
            key={item.id}
            id={`tab-${item.id}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${item.id}`}
            aria-disabled={item.disabled}
            disabled={item.disabled}
            tabIndex={isActive ? 0 : -1}
            onKeyDown={(event) => handleKeyDown(event, item.id)}
            onClick={() => {
              if (!item.disabled) {
                onSelect(item.id)
              }
            }}
            className={[styles.UnderlineTabs_Tab, isActive ? styles.UnderlineTabs_Tab__active : '']
              .filter(Boolean)
              .join(' ')}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
